import React, { useState, useEffect } from 'react';
import { Card, Spinner, Row, Col, Tabs, Tab, Button, Form, Alert } from 'react-bootstrap';
import { FaUser, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'components/authentication/signup-login/AuthContext';
import { updateUserProfile, getCurrentUser } from '../../../store/auth';
import CustomColorPalette from './CustomColorPalette';


const UserProfile = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedUserType, setEditedUserType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Fetch current user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated) return;
      
      setIsFetchingUser(true);
      try {
        const response = await getCurrentUser();
        const userData = response.data?.user;
        
        if (userData) {
          setCurrentUser(userData);
          setEditedName(userData.name || '');
          setEditedUserType(userData.usertype || '');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data');
      } finally {
        setIsFetchingUser(false);
      }
    };

    fetchUserData();
  }, [isAuthenticated]);

  // Fallback to context user if API call hasn't completed
  const displayUser = currentUser || user;

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedName(displayUser?.name || '');
    setEditedUserType(displayUser?.usertype || '');
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    // Validation
    if (!editedName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    if (!editedUserType.trim()) {
      setError('User type cannot be empty');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Call API to update user profile using auth.js
      const response = await updateUserProfile({
        name: editedName.trim(),
        usertype: editedUserType.trim()
      });

      if (response.data?.user) {
        const updatedUserData = response.data.user;
        
        // Update local state
        setCurrentUser(updatedUserData);
        
        // Update context if updateUser function exists
        if (updateUser) {
          updateUser(updatedUserData);
        }
        
        // Also update localStorage userData
        const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
        localStorage.setItem('userData', JSON.stringify({
          ...storedUserData,
          name: updatedUserData.name,
          usertype: updatedUserData.usertype
        }));
        
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.');
      console.error('Update profile error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <Spinner animation="border" role="status" className="me-2" />
        <span>Loading profile...</span>
      </div>
    );
  }

  if (isFetchingUser) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <Spinner animation="border" role="status" className="me-2" />
        <span>Loading user data...</span>
      </div>
    );
  }

  return (
    <div className="container my-5">
      {/* Profile Card */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  {/* Avatar */}
                  <div
                    className="d-flex align-items-center justify-content-center bg-primary text-white"
                    style={{
                      width: '60px',
                      height: '60px',
                      fontSize: '24px',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    {displayUser?.avatar ? (
                      <img
                        src={displayUser.avatar}
                        alt="User Avatar"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '0'
                        }}
                      />
                    ) : (
                      <FaUser />
                    )}
                  </div>

                  {/* Username */}
                  <div>
                    <h4 className="mb-0">{displayUser?.name || 'User'}</h4>
                    <small className="text-muted">{displayUser?.email}</small>
                  </div>
                </div>

                
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Alert Messages */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs Section */}
      <Row>
        <Col>
          <Tabs defaultActiveKey="details" id="user-profile-tabs" className="mb-3">
            {/* Tab 1: Details */}
            <Tab eventKey="details" title="Profile">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
  <h5 className="mb-0">User Profile</h5>
  
  <div>
    {!isEditing && (
      <Button 
        variant="outline-primary" 
        size="sm" 
        className="me-2"
        onClick={handleEdit}
      >
        <FaEdit className="me-1" /> Edit Profile
      </Button>
    )}

    {isEditing && (
      <>
        <Button 
          variant="success" 
          size="sm" 
          className="me-2"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Saving...
            </>
          ) : (
            <>
              <FaSave className="me-1" /> Save
            </>
          )}
        </Button>

        <Button 
          variant="secondary" 
          size="sm"
          onClick={handleCancel}
          disabled={isLoading}
        >
          <FaTimes className="me-1" /> Cancel
        </Button>
      </>
    )}
  </div>
</Card.Header>

                <Card.Body>
                  {/* Name Field */}
                  <Row className="mb-4">
                    <Col md={3}><strong>Name:</strong></Col>
                    <Col md={9}>
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          placeholder="Enter your name"
                          disabled={isLoading}
                        />
                      ) : (
                        <span>{displayUser?.name || 'Not set'}</span>
                      )}
                    </Col>
                  </Row>

                  {/* Email Field (Read-only) */}
                  <Row className="mb-4">
                    <Col md={3}><strong>Email:</strong></Col>
                    <Col md={9}>
                      <span>{displayUser?.email}</span>
                      <br />
                      <small className="text-muted">Email cannot be changed</small>
                    </Col>
                  </Row>

                  {/* User Type Field */}
                  <Row>
                    <Col md={3}><strong>User Type:</strong></Col>
                    <Col md={9}>
                      {isEditing ? (
                        <Form.Select
                          value={editedUserType}
                          onChange={(e) => setEditedUserType(e.target.value)}
                          disabled={isLoading}
                        >
                          <option value="">Select user type</option>
                          <option value="Student">Student</option>
                          <option value="Industry">Industry</option>
                          <option value="Journalist">Journalist</option>
                          <option value="Civil Societies">Civil Societies</option>
                          <option value="Others">Others</option>
                        </Form.Select>
                      ) : (
                        <span className="badge bg-primary">
                          {displayUser?.usertype || 'Not set'}
                        </span>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* Tab 2: Configuration */}
            <Tab eventKey="configuration" title="Configuration">
              <CustomColorPalette />
            </Tab>
           
          </Tabs>
        </Col>
      </Row>
    </div>
  );
};

export default UserProfile;